"use client";

import * as React from "react";
import {
	RowData,
	flexRender,
	getCoreRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { ChevronDownIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { type AnyColumnDef } from "./data-table-types";
import { Checkbox } from "./checkbox";
import {
	Command,
	CommandEmpty,
	CommandInput,
	CommandItem,
	CommandList,
	CommandLoading,
} from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Spinner } from "./spinner";
import { Table, type TableRowProps } from "./table";
import {
	TooltipContent,
	TooltipProvider,
	TooltipRoot,
	TooltipTrigger,
} from "./tooltip";

const SELECT_COLUMN_WIDTH = 40;

// Applied to CommandItem (not Table.Row). Each row is a CommandItem wrapped
// `asChild` around a Table.Row (`<tr>`), so cmdk's `<div>`-oriented classes
// (`flex`, `px-2 py-1.5`, `cursor-default`) get merged into the row and, because
// they land last in Table.Row's own `cn()`, beat the row's `table`/`p-0`. That
// turns the row into a flexbox with extra padding, so its columns stop lining up
// with the `table-layout: fixed` header. Passing this string to CommandItem puts
// it last in CommandItem's own `cn()`, so `table` beats `flex` and `p-0` beats
// `px-2 py-1.5`; `cursor-not-allowed` on a disabled row is applied later still,
// so it keeps winning over `cursor-pointer`. Do not delete this as "redundant".
const ROW_RESET_CLASSNAME = "table w-full table-fixed p-0 cursor-pointer";

export type MultiSelectTableComboboxProps<TData extends RowData> = {
	items: TData[];
	columns: AnyColumnDef<TData>[];
	selectedIds: string[];
	onSelectedIdsChange: (selectedIds: string[]) => void;
	getItemId?: (item: TData, index: number) => string;
	getSearchValue?: (item: TData) => string;
	filterItem?: (item: TData, searchValue: string) => boolean;
	searchValue?: string;
	defaultSearchValue?: string;
	onSearchValueChange?: (searchValue: string) => void;
	placeholder?: string;
	searchPlaceholder?: string;
	emptyState?: React.ReactNode;
	loadingState?: React.ReactNode;
	isLoading?: boolean;
	disabled?: boolean;
	/**
	 * Optional per-row gate. When it returns `true` the row is greyed out, its
	 * checkbox is disabled and it cannot be (de)selected (incl. via "select
	 * all"). Defaults to all rows enabled.
	 */
	isItemDisabled?: (item: TData, index: number) => boolean;
	/**
	 * Tooltip content shown on hover for a disabled row. Only used when
	 * `isItemDisabled` returns `true` for that row.
	 */
	getItemDisabledReason?: (item: TData) => React.ReactNode;
	minCharacters?: number;
	showTableHeader?: boolean;
	/**
	 * When true, the table fills the popover width instead of using a fixed width summed from the column
	 * sizes. Columns whose id is listed in `flexibleColumnIds` absorb the spare width; all other columns
	 * keep their fixed pixel `size`.
	 * @default false
	 */
	fillWidth?: boolean;
	/**
	 * Column ids that should flex to absorb spare width when `fillWidth` is on. No effect unless `fillWidth` is true.
	 * Listed columns are left width-less, so `table-layout: fixed` shares the width left after the fixed columns
	 * *equally* between them and ignores each one's `size`. To give a single column more room, leave only that one
	 * flexible and keep the rest fixed.
	 * @default []
	 */
	flexibleColumnIds?: string[];
	rowHeight?: TableRowProps["height"];
	renderValue?: (props: {
		selectedIds: string[];
		selectedCount: number;
	}) => React.ReactNode;
	selectAllLabel?: string;
	selectRowLabel?: (item: TData) => string;
	id?: string;
	"aria-label"?: string;
	"data-testid"?: string;
	className?: string;
	triggerClassName?: string;
	popoverClassName?: string;
	tableClassName?: string;
};

function defaultGetItemId<TData>(item: TData, index: number): string {
	if (typeof item === "object" && item !== null) {
		const id = (item as Record<string, unknown>).id;
		if (typeof id === "string" || typeof id === "number") {
			return String(id);
		}
	}

	return String(index);
}

export function MultiSelectTableCombobox<TData extends RowData>({
	items,
	columns,
	selectedIds,
	onSelectedIdsChange,
	getItemId = defaultGetItemId,
	getSearchValue,
	filterItem,
	searchValue,
	defaultSearchValue = "",
	onSearchValueChange,
	placeholder = "Select options",
	searchPlaceholder = "Search...",
	emptyState = "No results found.",
	loadingState,
	isLoading = false,
	disabled = false,
	isItemDisabled,
	getItemDisabledReason,
	minCharacters = 0,
	showTableHeader = true,
	fillWidth = false,
	flexibleColumnIds,
	rowHeight = "sm",
	renderValue,
	selectAllLabel = "Select all filtered rows",
	selectRowLabel,
	id,
	"aria-label": ariaLabel,
	"data-testid": testId,
	className,
	triggerClassName,
	popoverClassName,
	tableClassName,
}: MultiSelectTableComboboxProps<TData>) {
	const generatedId = React.useId();
	const triggerId = id ?? generatedId;
	const listboxId = `${triggerId}-listbox`;
	const [isOpen, setIsOpen] = React.useState(false);
	const [internalSearchValue, setInternalSearchValue] =
		React.useState(defaultSearchValue);
	const searchInputRef = React.useRef<HTMLInputElement>(null);

	const currentSearchValue = searchValue ?? internalSearchValue;
	const normalizedSearchValue = currentSearchValue.trim().toLowerCase();
	const selectedIdSet = React.useMemo(
		() => new Set(selectedIds),
		[selectedIds],
	);

	const getRowId = React.useCallback(
		(item: TData, index: number) => getItemId(item, index),
		[getItemId],
	);

	const filteredItems = React.useMemo(() => {
		if (normalizedSearchValue.length < minCharacters) {
			return [];
		}

		if (normalizedSearchValue.length === 0) {
			return items;
		}

		return items.filter((item) => {
			if (filterItem) {
				return filterItem(item, currentSearchValue);
			}

			const value = getSearchValue?.(item) ?? "";
			return value.toLowerCase().includes(normalizedSearchValue);
		});
	}, [
		currentSearchValue,
		filterItem,
		getSearchValue,
		items,
		minCharacters,
		normalizedSearchValue,
	]);

	// eslint-disable-next-line react-hooks/incompatible-library -- TanStack Table returns a non-memoizable instance
	const table = useReactTable({
		columns,
		data: filteredItems,
		getCoreRowModel: getCoreRowModel(),
		getRowId,
	});

	const { rows } = table.getRowModel();
	const filteredIds = React.useMemo(() => rows.map((row) => row.id), [rows]);
	// Derived from `items` (not the filtered `rows`) keyed by `getRowId`, so a
	// disabled id stays tracked even when it's filtered out of view. This keeps
	// deselection of an already-selected-but-now-disabled id reliable.
	const disabledIdSet = React.useMemo(() => {
		if (!isItemDisabled) {
			return new Set<string>();
		}
		const ids = new Set<string>();
		items.forEach((item, index) => {
			if (isItemDisabled(item, index)) {
				ids.add(getRowId(item, index));
			}
		});
		return ids;
	}, [items, isItemDisabled, getRowId]);
	const tableWidth = React.useMemo(
		() =>
			`${SELECT_COLUMN_WIDTH + table.getAllLeafColumns().reduce((width, column) => width + column.getSize(), 0)}px`,
		[table],
	);
	const flexibleColumnIdSet = React.useMemo(
		() => new Set(flexibleColumnIds ?? []),
		[flexibleColumnIds],
	);
	// When filling the popover width, drop the fixed table/header/body/row width so the table is `w-full`;
	// otherwise keep the summed fixed width (unchanged default behaviour).
	const fixedWidthStyle = fillWidth ? undefined : { width: tableWidth };
	// Flexible columns in fillWidth mode are left width-less on purpose: the header
	// and every row are `table-layout: fixed` boxes, so each one splits whatever
	// width is left after the sized columns *equally* between the width-less
	// columns — and does so identically in the header and every row, which is what
	// keeps the columns aligned. Do NOT reintroduce a calc() / percentage width
	// here: fixed table layout treats a width mixing `%` and `px` as `auto` and
	// falls back to the equal split in the header anyway, so it only ever appeared
	// to work when the row had accidentally become a flexbox (APP-24681).
	const columnStyle = (columnId: string, size: number) =>
		fillWidth && flexibleColumnIdSet.has(columnId)
			? undefined
			: { width: `${size}px` };
	// "Select all" and the header tri-state only consider selectable (non-disabled) rows.
	const selectableFilteredIds = React.useMemo(
		() => filteredIds.filter((itemId) => !disabledIdSet.has(itemId)),
		[filteredIds, disabledIdSet],
	);
	const filteredSelectedCount = React.useMemo(
		() =>
			selectableFilteredIds.filter((itemId) => selectedIdSet.has(itemId))
				.length,
		[selectableFilteredIds, selectedIdSet],
	);
	const hasSelectableFilteredItems = selectableFilteredIds.length > 0;
	const areAllFilteredSelected =
		hasSelectableFilteredItems &&
		filteredSelectedCount === selectableFilteredIds.length;
	const areSomeFilteredSelected =
		filteredSelectedCount > 0 &&
		filteredSelectedCount < selectableFilteredIds.length;

	const handleSearchValueChange = (nextSearchValue: string) => {
		if (searchValue === undefined) {
			setInternalSearchValue(nextSearchValue);
		}
		onSearchValueChange?.(nextSearchValue);
	};

	const handleOpenChange = (nextOpen: boolean) => {
		if (disabled) {
			setIsOpen(false);
			return;
		}
		setIsOpen(nextOpen);
	};

	const handleToggleItem = (itemId: string) => {
		const isCurrentlySelected = selectedIdSet.has(itemId);
		// Disabled rows can't be newly selected, but an already-selected id that
		// later became disabled must still be removable.
		if (disabledIdSet.has(itemId) && !isCurrentlySelected) {
			return;
		}
		const nextSelectedIds = isCurrentlySelected
			? selectedIds.filter((selectedId) => selectedId !== itemId)
			: [...selectedIds, itemId];
		onSelectedIdsChange(nextSelectedIds);
	};

	const handleToggleAllFiltered = (checked: boolean | "indeterminate") => {
		if (!hasSelectableFilteredItems) {
			return;
		}

		if (checked) {
			onSelectedIdsChange([
				...selectedIds,
				...selectableFilteredIds.filter((itemId) => !selectedIdSet.has(itemId)),
			]);
			return;
		}

		const filteredIdSet = new Set(selectableFilteredIds);
		onSelectedIdsChange(
			selectedIds.filter((selectedId) => !filteredIdSet.has(selectedId)),
		);
	};

	const selectedCount = selectedIds.length;
	const triggerContent =
		renderValue?.({ selectedIds, selectedCount }) ??
		(selectedCount > 0
			? `${selectedCount.toLocaleString()} selected`
			: placeholder);

	return (
		<div className={cn("w-full", className)}>
			<Popover open={isOpen} onOpenChange={handleOpenChange}>
				<PopoverTrigger asChild>
					<button
						id={triggerId}
						type="button"
						role="combobox"
						aria-expanded={isOpen}
						aria-controls={listboxId}
						aria-label={ariaLabel ?? placeholder}
						disabled={disabled}
						data-testid={testId && `${testId}-trigger`}
						className={cn(
							"flex min-h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-left text-sm text-neutral-700 outline-none transition-[color,box-shadow]",
							"focus-visible:ring-2 focus-visible:ring-blue-200 disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:text-neutral-500 disabled:opacity-70",
							triggerClassName,
						)}
					>
						<span
							className={cn(
								"min-w-0 flex-1 truncate",
								selectedCount === 0 && "text-neutral-500",
							)}
						>
							{triggerContent}
						</span>
						<ChevronDownIcon
							className="size-4 shrink-0 text-neutral-400"
							aria-hidden="true"
						/>
					</button>
				</PopoverTrigger>
				<PopoverContent
					id={listboxId}
					align="start"
					sideOffset={4}
					className={cn(
						"w-[var(--radix-popover-trigger-width)] min-w-[min(640px,calc(100vw-2rem))] max-w-[calc(100vw-2rem)] p-0",
						popoverClassName,
					)}
					onOpenAutoFocus={(event) => {
						// Radix focuses the content box, which leaves the user a second click
						// away from typing, so the search box is focused directly instead.
						event.preventDefault();
						searchInputRef.current?.focus({ preventScroll: true });
					}}
				>
					<Command shouldFilter={false}>
						<CommandInput
							ref={searchInputRef}
							value={currentSearchValue}
							onValueChange={handleSearchValueChange}
							placeholder={searchPlaceholder}
							data-testid={testId && `${testId}-search`}
						/>
						<CommandList className="max-h-[min(360px,calc(var(--radix-popover-content-available-height)-4rem))] overflow-x-auto">
							{isLoading ? (
								<CommandLoading className="flex items-center justify-center gap-2 py-8 text-sm text-neutral-500">
									{loadingState ?? (
										<>
											<Spinner className="size-4" />
											<span>Loading...</span>
										</>
									)}
								</CommandLoading>
							) : filteredItems.length === 0 ? (
								<CommandEmpty className="py-8 text-center text-sm text-neutral-500">
									{emptyState}
								</CommandEmpty>
							) : (
								<TooltipProvider delayDuration={0}>
									<Table
										className={cn(
											"table-fixed",
											fillWidth && "w-full",
											tableClassName,
										)}
										style={fillWidth ? undefined : { minWidth: tableWidth }}
										data-testid={testId && `${testId}-table`}
									>
										{showTableHeader ? (
											<Table.Header style={fixedWidthStyle}>
												<Table.Column
													className="w-10"
													style={{ width: SELECT_COLUMN_WIDTH }}
												>
													<Checkbox
														size="sm"
														checked={
															areAllFilteredSelected ||
															(areSomeFilteredSelected
																? "indeterminate"
																: false)
														}
														onCheckedChange={handleToggleAllFiltered}
														aria-label={selectAllLabel}
														disabled={!hasSelectableFilteredItems}
													/>
												</Table.Column>
												{table.getHeaderGroups().map((headerGroup) =>
													headerGroup.headers.map((header) => (
														<Table.Column
															key={header.id}
															style={columnStyle(
																header.column.id,
																header.column.getSize(),
															)}
														>
															{header.isPlaceholder
																? null
																: flexRender(
																		header.column.columnDef.header,
																		header.getContext(),
																	)}
														</Table.Column>
													)),
												)}
											</Table.Header>
										) : null}
										<Table.Body style={fixedWidthStyle}>
											{rows.map((row) => {
												const item = row.original;
												const isSelected = selectedIdSet.has(row.id);
												const isRowDisabled = disabledIdSet.has(row.id);
												const disabledReason = isRowDisabled
													? getItemDisabledReason?.(item)
													: undefined;
												const tableRow = (
													<Table.Row
														height={rowHeight}
														interactive={!isRowDisabled}
														data-state={isSelected ? "selected" : "unselected"}
														aria-disabled={isRowDisabled || undefined}
														className={cn(
															"aria-selected:bg-neutral-50 data-[state=selected]:bg-neutral-50",
															isRowDisabled && "cursor-not-allowed opacity-50",
														)}
														style={fixedWidthStyle}
													>
														<Table.Cell style={{ width: SELECT_COLUMN_WIDTH }}>
															<Checkbox
																size="sm"
																checked={isSelected}
																// Allow unchecking an already-selected id that later
																// became disabled; only block selecting new ones.
																disabled={isRowDisabled && !isSelected}
																onCheckedChange={() => handleToggleItem(row.id)}
																onClick={(event) => event.stopPropagation()}
																aria-label={
																	selectRowLabel?.(item) ??
																	`Select row ${row.id}`
																}
															/>
														</Table.Cell>
														{row.getVisibleCells().map((cell) => (
															<Table.Cell
																key={cell.id}
																className="truncate"
																style={columnStyle(
																	cell.column.id,
																	cell.column.getSize(),
																)}
															>
																{flexRender(
																	cell.column.columnDef.cell,
																	cell.getContext(),
																)}
															</Table.Cell>
														))}
													</Table.Row>
												);

												// Disabled rows show a hover tooltip explaining why they
												// can't be picked. The Radix trigger must attach directly to
												// the <tr>, so TooltipTrigger wraps Table.Row *inside*
												// CommandItem (both use `asChild`, so no extra DOM is added
												// between <tbody> and <tr>).
												if (isRowDisabled && disabledReason != null) {
													return (
														<TooltipRoot key={row.id}>
															<CommandItem
																value={row.id}
																onSelect={() => handleToggleItem(row.id)}
																asChild
																className={ROW_RESET_CLASSNAME}
																data-testid={
																	testId && `${testId}-row-${row.id}`
																}
															>
																<TooltipTrigger asChild>
																	{tableRow}
																</TooltipTrigger>
															</CommandItem>
															{/* Sits above the popover (z-[2000]) so it
															   isn't hidden behind the dropdown. Opens below
															   the row so it never covers the header row
															   (APP-24681); Radix still flips it when there's
															   no room below. */}
															<TooltipContent
																side="bottom"
																className="z-[2050]"
															>
																{disabledReason}
															</TooltipContent>
														</TooltipRoot>
													);
												}

												return (
													<CommandItem
														key={row.id}
														value={row.id}
														onSelect={() => handleToggleItem(row.id)}
														asChild
														className={ROW_RESET_CLASSNAME}
														data-testid={testId && `${testId}-row-${row.id}`}
													>
														{tableRow}
													</CommandItem>
												);
											})}
										</Table.Body>
									</Table>
								</TooltipProvider>
							)}
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>
		</div>
	);
}

MultiSelectTableCombobox.displayName = "MultiSelectTableCombobox";
